import { createContext } from "react";
import React, { useContext, useState, useEffect } from "react";
import { UrlContext } from "./UrlContext";
import { redirect, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import reactGa from "react-ga4";

export const ConsultantContext = createContext();

export const ConsultantProvider = ({ children }) => {
  const { API_URL, URI } = useContext(UrlContext);
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(true);
  const [tokenId, setTokenId] = useState(null);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [accessToken, setAccessToken] = useState("");

  // Function to decode JWT
  const decodeJWT = (token) => {
    try {
      // JWT has 3 parts separated by dots: header.payload.signature
      const base64Url = token.split(".")[1]; // Get the payload
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return null;
    }
  };

  // Function to check if token has expired
  const isTokenExpired = (token) => {
    try {
      const decodedToken = decodeJWT(token);
      if (!decodedToken || !decodedToken.exp) return false;

      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch (error) {
      return true;
    }
  };
  const redirectToLogin = () => {
    localStorage.clear();
    navigate("/login");
  };
  // Function to validate existing access token
  const validateAndRedirectWithAccessToken = (token) => {
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.log("Token expired, removing from storage");
      localStorage.removeItem("AccessToken");
      localStorage.removeItem("linkedinToken");
      redirectToLogin();
      setLoading(false);
      return;
    }

    // Decode the JWT
    const decodedToken = decodeJWT(token);

    if (!decodedToken || !decodedToken.id) {
      console.error("Invalid token or missing ID");
      localStorage.removeItem("AccessToken");
      localStorage.removeItem("linkedinToken");
      redirectToLogin();
      setLoading(false);
      return;
    }

    const tokenUserId = decodedToken.id.toString();
    setTokenId(tokenUserId);
    setAccessToken(token);
    setIsTokenValid(true);
    setLoading(false);

    // Redirect to consultant page with ID
    navigate(`/app/${tokenUserId}`);
  };

  // Function to get access token from LinkedIn token
  const getAccessTokenFromLinkedIn = (linkedinToken) => {
    setLoading(true);

    fetch(`${API_URL}consultant-data-with-linkedin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ AT: linkedinToken }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          // Store the new access token
          localStorage.setItem("AccessToken", data.token);

          // Validate and redirect with the new token
          validateAndRedirectWithAccessToken(data.token);

          // Optional: Track with Google Analytics
          if (data.expert_info?.id) {
            reactGa.event({
              category: "Consultant",
              action: "Login with LinkedIn",
              parameters: {
                consultant_id: `${data.expert_info.id}`,
              },
            });
          }
        } else if (data.message === "Profile doesn't exist") {
          console.error("Profile doesn't exist");
          localStorage.clear();
          navigate("/invalid-consultant");
          setLoading(false);
          Swal.fire({
            title: "Profile Not Found",
            icon: "error",
            text: "Your consultant profile doesn't exist. Please contact support.",
            timer: 3000,
          });
        } else {
          console.error("Error getting access token:", data.message);
          localStorage.clear();
          redirectToLogin();
          setLoading(false);
          Swal.fire({
            title: "Error",
            icon: "error",
            text: `${data.message || "Authentication failed"}`,
            timer: 2000,
          });
        }
      })
      .catch((err) => {
        console.error("Network error:", err.message);
        localStorage.clear();
        redirectToLogin();
        setLoading(false);
        Swal.fire({
          title: "Network Error",
          icon: "error",
          text: "Unable to connect to server. Please try again.",
          timer: 2000,
        });
      });
  };

  // Function to handle LinkedIn code validation
  const handleLinkedInCode = (code) => {
    setLoading(true);

    const headers = new Headers();
    headers.append("Content-Type", "application/json");

    fetch(`${API_URL}consultant-connection`, {
      method: "POST",
      body: JSON.stringify({
        code: code,
        uri: `${URI}app`,
      }),
      headers: headers,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.data?.access_token) {
          // Store LinkedIn token
          localStorage.setItem("linkedinToken", data.data.access_token);

          // Get access token using LinkedIn token
          getAccessTokenFromLinkedIn(data.data.access_token);
        } else {
          console.error("No access token received from LinkedIn");
          redirectToLogin();
          setLoading(false);
          Swal.fire({
            title: "LinkedIn Authentication Failed",
            icon: "error",
            text: "Unable to authenticate with LinkedIn. Please try again.",
            timer: 2000,
          });
        }
      })
      .catch((err) => {
        console.error("LinkedIn connection error:", err.message);
        redirectToLogin();
        setLoading(false);
        Swal.fire({
          title: "Connection Error",
          icon: "error",
          text: "Unable to connect with LinkedIn. Please try again.",
          timer: 2000,
        });
      });
  };

  // Main authentication logic
  useEffect(() => {
    const initializeAuth = () => {
      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const codeFromURL = urlParams.get("code");

      // Get tokens from localStorage
      const storedAccessToken = localStorage.getItem("AccessToken");
      const storedLinkedInToken = localStorage.getItem("linkedinToken");

      // Option 1: URL contains LinkedIn code
      if (codeFromURL) {
        console.log("LinkedIn code found in URL, processing...");
        handleLinkedInCode(codeFromURL);
        return;
      }

      // Option 2: AccessToken exists in localStorage
      if (storedAccessToken) {
        console.log("AccessToken found in localStorage, validating...");
        validateAndRedirectWithAccessToken(storedAccessToken);
        return;
      }

      // Option 3: LinkedIn token exists but no AccessToken
      if (storedLinkedInToken && storedLinkedInToken !== "undefined") {
        console.log("LinkedIn token found, getting AccessToken...");
        getAccessTokenFromLinkedIn(storedLinkedInToken);
        return;
      }

      // Option 4: No authentication found, redirect to login
      console.log("No authentication found, redirecting to login");
      redirectToLogin();
      setLoading(false);
    };

    initializeAuth();
  }, []); // Empty dependency array to run only once on mount

  // Clean up invalid LinkedIn tokens
  useEffect(() => {
    const linkedinToken = localStorage.getItem("linkedinToken");
    if (linkedinToken === "undefined") {
      localStorage.removeItem("linkedinToken");
    }
  }, []);

  // Function to logout
  const logout = () => {
    localStorage.clear();
    setTokenId(null);
    setIsTokenValid(false);
    setAccessToken("");
    redirectToLogin();
  };

  return (
    <ConsultantContext.Provider
      value={{
        loading,
        tokenId,
        isTokenValid,
        accessToken,
        logout,
        decodeJWT,
        isTokenExpired,
      }}
    >
      {children}
    </ConsultantContext.Provider>
  );
};
